import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Copy, KeyRound, ShieldAlert, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Cabecera } from "@/components/propuesta/cabecera";
import { Aviso, Estado } from "@/components/propuesta/piezas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_app/usuarios")({ component: Usuarios });

// ===========================================================================
// Quién entra al módulo.
//
// Lo más simple que funcione: dar de alta a alguien de contabilidad, ponerle
// un rol y poder cortarle el paso. Hasta ahora eso se hacía por terminal, que
// es tanto como decir que lo hacía una sola persona.
//
// Los cinco roles son los MISMOS del ERP y se copiaron tal cual, para que el
// día que este módulo se mude allá el mapeo sea directo. No conviene inventar
// roles nuevos aquí.
// ===========================================================================

const ROLES = [
  "administrador",
  "gerente",
  "jefe",
  "operativo",
  "visor",
] as const;

type Rol = (typeof ROLES)[number];

type Usuario = {
  id: string;
  email: string;
  nombre: string;
  rol: Rol;
  activo: boolean;
  creado: string;
  sesiones: number;
  soyYo: boolean;
};

function Usuarios() {
  const qc = useQueryClient();
  const { data, isPending } = api.usuarios.useQuery(["usuarios"]);
  const d = data?.status === 200 ? data.body : null;
  const sinPermiso = data?.status === 403;

  const crear = api.crearUsuario.useMutation();
  const rol = api.cambiarRol.useMutation();
  const acceso = api.cambiarAcceso.useMutation();
  const clave = api.restablecerClave.useMutation();

  const [abierto, setAbierto] = useState(false);
  const [email, setEmail] = useState("");
  const [nombre, setNombre] = useState("");
  const [rolNuevo, setRolNuevo] = useState<Rol>("operativo");

  /**
   * La contraseña recién puesta, en pantalla hasta que se cierre.
   *
   * En un aviso y no en un toast: un toast se va solo a los cinco segundos y
   * esta contraseña no se puede volver a ver. Lo guardado es un hash, así que
   * si se pierde hay que poner otra.
   */
  const [recien, setRecien] = useState<{ quien: string; clave: string } | null>(
    null,
  );

  const alFallar = (r: { body?: { message?: string } } | undefined) =>
    toast.error(r?.body?.message ?? "No se pudo");

  const darDeAlta = () => {
    crear.mutate(
      { body: { email, nombre, rol: rolNuevo } },
      {
        onSuccess: (r: {
          status: number;
          body: { clave?: string | null; dice?: string; message?: string };
        }) => {
          if (r.status !== 200) {
            alFallar(r);
            return;
          }
          toast.success(r.body.dice ?? "Usuario creado");
          if (r.body.clave) setRecien({ quien: email, clave: r.body.clave });
          setEmail("");
          setNombre("");
          setAbierto(false);
          qc.invalidateQueries();
        },
        onError: () => toast.error("No se pudo crear"),
      },
    );
  };

  if (sinPermiso) {
    return (
      <>
        <Cabecera migas={["Control"]} titulo="Usuarios" />
        <Aviso tono="warn">
          <ShieldAlert className="mt-px size-4 shrink-0" />
          <span>
            Esta pantalla es solo para quien administra. Si necesitás dar de
            alta a alguien, pedíselo a un administrador.
          </span>
        </Aviso>
      </>
    );
  }

  return (
    <>
      <Cabecera
        migas={["Control"]}
        titulo="Usuarios"
        acciones={
          <Button size="sm" onClick={() => setAbierto((v) => !v)}>
            <UserPlus className="mr-2 size-4" />
            Dar de alta
          </Button>
        }
      />

      <p className="-mt-2 max-w-3xl text-[13px] text-muted-foreground">
        No hay registro abierto: las cuentas las crea alguien de acá. Desactivar
        a una persona le corta el paso en el acto —el guard mira su estado en
        cada petición— y le cierra las sesiones que tenga abiertas.
      </p>

      {recien ? (
        <Aviso tono="info">
          <KeyRound className="mt-px size-4 shrink-0" />
          <span className="flex flex-wrap items-center gap-2">
            <span>
              Contraseña de <b>{recien.quien}</b>:
            </span>
            <code className="rounded bg-background/60 px-2 py-0.5 font-mono text-[12.5px]">
              {recien.clave}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(recien.clave);
                toast.success("Copiada");
              }}
            >
              <Copy className="mr-1.5 size-3.5" />
              Copiar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setRecien(null)}>
              Ya la anoté
            </Button>
            <span className="w-full text-[11px]">
              No se vuelve a mostrar: lo que se guarda es un hash, así que nadie
              —tampoco quien administra— puede leerla después.
            </span>
          </span>
        </Aviso>
      ) : null}

      {abierto ? (
        <Card>
          <CardHeader>
            <CardTitle className="font-medium text-base">
              Alguien nuevo
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-[12px] text-muted-foreground">
              Correo
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="alguien@ecoplaza.pe"
                className="h-9 w-64 rounded-md border bg-background px-2 text-[13px]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] text-muted-foreground">
              Nombre
              <input
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="Nombre Apellido"
                className="h-9 w-64 rounded-md border bg-background px-2 text-[13px]"
              />
            </label>
            <label className="flex flex-col gap-1 text-[12px] text-muted-foreground">
              Rol
              <select
                value={rolNuevo}
                onChange={(e) => setRolNuevo(e.target.value as Rol)}
                className="h-9 rounded-md border bg-background px-2 text-[13px]"
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
            <Button
              disabled={!email || nombre.length < 2 || crear.isPending}
              onClick={darDeAlta}
            >
              {crear.isPending ? "Creando…" : "Crear"}
            </Button>
            <Button variant="ghost" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <p className="w-full text-[11px] text-muted-foreground">
              La contraseña la genera el sistema y se dice una sola vez.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="font-medium text-base">
            {d ? `${d.usuarios.length} personas con cuenta` : "Usuarios"}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          {isPending ? (
            <div className="space-y-2 px-6">
              {["a", "b", "c"].map((k) => (
                <Skeleton key={k} className="h-10" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead className="border-b text-[11px] text-muted-foreground uppercase tracking-wide">
                  <tr>
                    <th className="py-2 pl-6 text-left font-medium">Correo</th>
                    <th className="py-2 pl-6 text-left font-medium">Nombre</th>
                    <th className="py-2 pl-6 text-left font-medium">Rol</th>
                    <th className="py-2 pl-6 text-right font-medium">
                      Sesiones
                    </th>
                    <th className="py-2 pl-6 text-left font-medium">Acceso</th>
                    <th className="py-2 pr-6" />
                  </tr>
                </thead>
                <tbody>
                  {(d?.usuarios ?? []).map((u: Usuario) => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="py-2 pl-6">
                        {u.email}
                        {u.soyYo ? (
                          <span className="ml-1.5 text-[11px] text-muted-foreground">
                            (vos)
                          </span>
                        ) : null}
                      </td>
                      <td className="py-2 pl-6">{u.nombre}</td>
                      <td className="py-2 pl-6">
                        <select
                          value={u.rol}
                          onChange={(e) =>
                            rol.mutate(
                              {
                                params: { id: u.id },
                                body: { rol: e.target.value as Rol },
                              },
                              {
                                onSuccess: (r: {
                                  status: number;
                                  body: { dice?: string; message?: string };
                                }) => {
                                  if (r.status !== 200) {
                                    alFallar(r);
                                    qc.invalidateQueries();
                                    return;
                                  }
                                  toast.success(r.body.dice ?? "Rol cambiado");
                                  qc.invalidateQueries();
                                },
                                onError: () =>
                                  toast.error("No se pudo cambiar el rol"),
                              },
                            )
                          }
                          className="h-8 rounded-md border bg-background px-2 text-[12.5px]"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="py-2 pl-6 text-right tabular-nums">
                        {u.sesiones}
                      </td>
                      <td className="py-2 pl-6">
                        {u.activo ? (
                          <Estado tono="ok">entra</Estado>
                        ) : (
                          <Estado tono="bad">sin acceso</Estado>
                        )}
                      </td>
                      <td className="py-2 pr-6 text-right">
                        <div className="flex justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              clave.mutate(
                                { params: { id: u.id }, body: {} },
                                {
                                  onSuccess: (r: {
                                    status: number;
                                    body: {
                                      clave?: string;
                                      dice?: string;
                                      message?: string;
                                    };
                                  }) => {
                                    if (r.status !== 200 || !r.body.clave) {
                                      alFallar(r);
                                      return;
                                    }
                                    setRecien({
                                      quien: u.email,
                                      clave: r.body.clave,
                                    });
                                    toast.success(r.body.dice ?? "Cambiada");
                                    qc.invalidateQueries();
                                  },
                                  onError: () =>
                                    toast.error("No se pudo cambiar"),
                                },
                              )
                            }
                          >
                            <KeyRound className="mr-1.5 size-3.5" />
                            Contraseña
                          </Button>
                          <Button
                            size="sm"
                            variant={u.activo ? "ghost" : "secondary"}
                            // Cortarse el paso a uno mismo no tiene vuelta
                            // atrás: la sesión se corta en la siguiente
                            // petición y no queda pantalla desde donde
                            // deshacerlo.
                            disabled={u.soyYo && u.activo}
                            onClick={() =>
                              acceso.mutate(
                                {
                                  params: { id: u.id },
                                  body: { activo: !u.activo },
                                },
                                {
                                  onSuccess: (r: {
                                    status: number;
                                    body: { dice?: string; message?: string };
                                  }) => {
                                    if (r.status !== 200) {
                                      alFallar(r);
                                      return;
                                    }
                                    toast.success(r.body.dice ?? "Hecho");
                                    qc.invalidateQueries();
                                  },
                                  onError: () => toast.error("No se pudo"),
                                },
                              )
                            }
                          >
                            {u.activo ? "Quitar el acceso" : "Devolver acceso"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
